import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { sendContactMessage } from '../../services/contactApi';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Text,
  Textarea,
  Button,
  Stack,
  Heading,
  useToast
} from '@chakra-ui/react';

const EmailForm = () => {
  const toast = useToast();

  // Form validation schema
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .min(2, 'Name must be at least 2 characters'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    subject: Yup.string()
      .required('Subject is required')
      .min(5, 'Subject must be at least 5 characters'),
    message: Yup.string()
      .required('Message is required')
      .min(10, 'Message must be at least 10 characters')
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
      company: ''
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        await sendContactMessage({
          name: values.name,
          email: values.email,
          subject: values.subject,
          message: values.message,
          company: values.company
        });

        toast({
          title: 'Message sent',
          description: "We've received your message and will respond soon.",
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        resetForm();
      } catch (error) {
        const backendMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (typeof error?.response?.data === 'string'
            ? error.response.data
            : null);

        console.error('Failed to send email:', error);
        toast({
          title: 'Error',
          description:
            backendMessage ||
            error?.message ||
            "Failed to send message. Please try again later.",
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
      <Box>
          <Text color="blue.500" fontWeight="600" mb={4}>Get In Touch</Text>
          <Heading as="h2" fontSize="3xl" mb={8}>Fill The Form Below</Heading>
          <Box 
            as="form"
            onSubmit={formik.handleSubmit}
          >
              <Stack spacing={6}>
                <Box
                  position="absolute"
                  left="-10000px"
                  top="auto"
                  width="1px"
                  height="1px"
                  overflow="hidden"
                  aria-hidden="true"
                >
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    autoComplete="off"
                    tabIndex={-1}
                    onChange={formik.handleChange}
                    value={formik.values.company}
                  />
                </Box>
                <FormControl 
                  isInvalid={formik.touched.name && Boolean(formik.errors.name)}
                >
                  <FormLabel htmlFor="name">Your Name</FormLabel>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                    placeholder="Enter your name"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <Box color="orange.500" fontSize="sm" mt={1}>
                      {formik.errors.name}
                    </Box>
                  )}
                </FormControl>

                <FormControl 
                  isInvalid={formik.touched.email && Boolean(formik.errors.email)}
                >
                  <FormLabel htmlFor="email">Your Email</FormLabel>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    placeholder="Enter your email"
                  />
                  {formik.touched.email && formik.errors.email && (
                    <Box color="orange.500" fontSize="sm" mt={1}>
                      {formik.errors.email}
                    </Box>
                  )}
                </FormControl>

                <FormControl 
                  isInvalid={formik.touched.subject && Boolean(formik.errors.subject)}
                >
                  <FormLabel htmlFor="subject">Subject</FormLabel>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.subject}
                    placeholder="Enter subject"
                  />
                  {formik.touched.subject && formik.errors.subject && (
                    <Box color="orange.500" fontSize="sm" mt={1}>
                      {formik.errors.subject}
                    </Box>
                  )}
                </FormControl>

                <FormControl 
                  isInvalid={formik.touched.message && Boolean(formik.errors.message)}
                >
                  <FormLabel htmlFor="message">Message</FormLabel>
                  <Textarea
                    id="message"
                    name="message"
                    rows={5}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.message}
                    placeholder="Enter your message"
                  />
                  {formik.touched.message && formik.errors.message && (
                    <Box color="orange.500" fontSize="sm" mt={1}>
                      {formik.errors.message}
                    </Box>
                  )}
                </FormControl>

                <Text fontSize="sm" color="gray.600">
                  We'll reply within 1-2 business days.
                </Text>

                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  isLoading={formik.isSubmitting}
                  isDisabled={!formik.isValid || formik.isSubmitting}
                >
                  Submit
                </Button>
              </Stack>
          </Box>
      </Box>
  );
};

export default EmailForm;
